import React, { useState, useEffect, useRef } from 'react';
import InfiniteMenu from '../../components/InfiniteMenu/InfiniteMenu';
import publicService from '../../services/publicService';
import { extractApiList, extractApiPagination } from '../../utils/responseAdapters';
import { resolveImageUrl, onImageError } from '../../utils/imageUrl';
import '../../styles/ArtworksPage.css';

const CATEGORIES = [
  {
    id: 'allArts',
    label: 'Artworks',
    color: '#7c3aed',
    icon: '🎨',
    description: 'Just Explore',
  },
  {
    id: 'onSale',
    label: 'On Sale',
    color: '#10b981',
    icon: '🏷️',
    description: 'For Purchase',
  },
  {
    id: 'tokenized',
    label: 'TIT-TATS',
    color: '#f59e0b',
    icon: '🔮',
    description: 'Collectibles',
  },
];

function transformToMenuItems(arts) {
  if (!Array.isArray(arts) || arts.length === 0) return [];
  return arts.map(art => ({
    image: resolveImageUrl(art.image_url),
    link: '#',
    title: art.title || 'Untitled',
    description: art.description || art.status?.replace(/_/g, ' ') || '',
    artData: art,
  }));
}

function ArtDetailModal({ item, category, onClose }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!item) return null;
  const art = item.artData || item;

  const formatPrice = price => {
    if (price == null) return null;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'NPR' }).format(price);
  };

  const formatDate = dateStr => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  };

  return (
    <div className="art-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="art-modal"
        style={{ '--accent': category.color }}
        onClick={e => e.stopPropagation()}
      >
        <button className="art-modal-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        <div className="art-modal-image-wrap">
          <img
            src={resolveImageUrl(art.image_url)}
            alt={art.title || 'Artwork'}
            className="art-modal-image"
            onError={onImageError}
          />
          <span className="art-modal-badge" style={{ background: category.color }}>
            {category.icon} {category.label}
          </span>
        </div>

        <div className="art-modal-body">
          <h2 className="art-modal-title" style={{ color: category.color }}>
            {art.title || 'Untitled'}
          </h2>

          {art.description && (
            <p className="art-modal-description">{art.description}</p>
          )}

          <div className="art-modal-meta">
            {art.status && (
              <div className="art-modal-meta-row">
                <span className="meta-label">Status</span>
                <span className="meta-value meta-badge">
                  {art.status.replace(/_/g, ' ')}
                </span>
              </div>
            )}

            {art.price != null && (
              <div className="art-modal-meta-row">
                <span className="meta-label">Price</span>
                <span className="meta-value meta-price" style={{ color: '#10b981' }}>
                  {formatPrice(art.price)}
                </span>
              </div>
            )}

            {art.token_id && (
              <div className="art-modal-meta-row">
                <span className="meta-label">Token ID</span>
                <span className="meta-value meta-token" style={{ color: '#f59e0b' }}>
                  {art.token_id}
                </span>
              </div>
            )}

            {art.artist_name && (
              <div className="art-modal-meta-row">
                <span className="meta-label">Artist</span>
                <span className="meta-value">{art.artist_name}</span>
              </div>
            )}

            {formatDate(art.created_at) && (
              <div className="art-modal-meta-row">
                <span className="meta-label">Added</span>
                <span className="meta-value">{formatDate(art.created_at)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ArtworksPage() {
  const [activeTab, setActiveTab] = useState('allArts');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [modalItem, setModalItem] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  // Persists next_cursor across Reload presses without triggering re-renders.
  const cursorRef = useRef(null);

  const activeCategory = CATEGORIES.find(c => c.id === activeTab) || CATEGORIES[0];

  const handleTabChange = (id) => {
    if (id === activeTab) return;
    cursorRef.current = null;
    setRefreshCount(0);
    setModalItem(null);
    setActiveTab(id);
  };

  const handleRefresh = () => {
    setRefreshCount(n => n + 1);
  };

  const handleClientShuffle = () => {
    setItems(prev => [...prev].sort(() => Math.random() - 0.5));
  };

  const handleServerShuffle = async () => {
    // Fetch a fresh random batch from the backend using $sample
    setIsRefreshing(true);
    setError(null);
    cursorRef.current = null; // Reset cursor — shuffle is always from scratch
    try {
      const params = { limit: 30, shuffle: true };
      let res;
      if (activeTab === 'allArts') res = await publicService.getAllArtworks(params);
      else if (activeTab === 'onSale') res = await publicService.getArtworksOnSale(params);
      else res = await publicService.getTokenizedArt(params);

      const pageItems = extractApiList(res?.data);
      if (!pageItems.length) {
        setError('No artworks found in this category.');
      } else {
        setItems(transformToMenuItems(pageItems));
      }
    } catch {
      setError('Failed to load artworks. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    // First load of a tab (refreshCount === 0) → full blank + spinner.
    // Subsequent reloads (refreshCount > 0) → keep sphere, show spinning icon only.
    if (refreshCount === 0) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    if (refreshCount === 0) setItems([]);

    const fetchData = async () => {
      try {
        const params = { limit: 30, ...(cursorRef.current ? { cursor: cursorRef.current } : {}) };
        let res;
        if (activeTab === 'allArts') res = await publicService.getAllArtworks(params);
        else if (activeTab === 'onSale') res = await publicService.getArtworksOnSale(params);
        else res = await publicService.getTokenizedArt(params);

        if (cancelled) return;

        const pageItems = extractApiList(res?.data);
        const pagination = extractApiPagination(res?.data);

        // Advance or wrap-around the cursor for next Reload press.
        if (pagination?.has_more && pagination?.next_cursor) {
          cursorRef.current = pagination.next_cursor;
        } else {
          cursorRef.current = null; // wrap-around: next press starts from beginning
        }

        if (!pageItems.length) {
          setItems([]);
          setError('No artworks found in this category.');
        } else {
          setItems(transformToMenuItems(pageItems));
        }
      } catch {
        if (!cancelled) setError('Failed to load artworks. Please try again.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setIsRefreshing(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [activeTab, refreshCount]);

  // Show hint pill when sphere is ready; auto-dismiss after 6 s
  useEffect(() => {
    if (!loading && !error && items.length > 0) {
      setShowHint(true);
      const t = setTimeout(() => setShowHint(false), 6000);
      return () => clearTimeout(t);
    }
  }, [loading, error, items]);

  return (
    <div className="artworks-page">
      <div className="artworks-header">
        <h1 className="artworks-title">
          <span className="title-static">Explore</span>
          <span className="title-accent" style={{ color: activeCategory.color }}>
            {activeCategory.label}
          </span>
        </h1>
        <p className="artworks-subtitle">{activeCategory.description}</p>
      </div>

      <div className="artworks-tabs">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            className={`tab-btn ${activeTab === cat.id ? 'tab-btn--active' : ''}`}
            style={
              activeTab === cat.id
                ? {
                    '--tab-color': cat.color,
                    borderColor: cat.color,
                    color: cat.color,
                    background: `${cat.color}18`,
                    boxShadow: `0 0 18px ${cat.color}44, 0 0 40px ${cat.color}18`,
                  }
                : {}
            }
            onClick={() => handleTabChange(cat.id)}
          >
            <span className="tab-icon">{cat.icon}</span>
            <span className="tab-label">{cat.label}</span>
            {activeTab === cat.id && (
              <span className="tab-dot" style={{ background: cat.color }} />
            )}
          </button>
        ))}
      </div>

      <div className="artworks-stage">
        {loading && (
          <div className="artworks-state">
            <div
              className="loading-ring"
              style={{ borderTopColor: activeCategory.color }}
            />
            <p className="state-text" style={{ color: activeCategory.color }}>
              Loading artworks…
            </p>
          </div>
        )}

        {!loading && error && (
          <div className="artworks-state">
            <span className="state-emoji">🖼️</span>
            <p className="state-text">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <InfiniteMenu
            items={items}
            scale={1}
            onItemClick={item => setModalItem(item)}
          />
        )}

        {/* Action tray — Reload (next batch) + Shuffle (backend random) + Client shuffle */}
        {!loading && (
          <div className="stage-actions" style={{ '--accent': activeCategory.color }}>
            {items.length > 0 && (
              <button
                className="stage-action-btn"
                title="Shuffle current items"
                onClick={handleClientShuffle}
                aria-label="Shuffle current items"
              >
                <span className="stage-action-icon">⇄</span>
              </button>
            )}
            <button
              className="stage-action-btn"
              title="Random batch from server"
              onClick={handleServerShuffle}
              disabled={isRefreshing}
              aria-label="Random shuffle from server"
            >
              <span className={`stage-action-icon${isRefreshing ? ' is-spinning' : ''}`}> ౨</span>
            </button>
            <button
              className="stage-action-btn stage-action-btn--reload"
              title="Load next batch"
              onClick={handleRefresh}
              disabled={isRefreshing}
              aria-label="Load next batch"
            >
              <span className={`stage-action-icon${isRefreshing ? ' is-spinning' : ''}`}>↻</span>
            </button>
          </div>
        )}

        {showHint && (
          <div
            className={`artworks-hint ${showHint ? 'hint-visible' : ''}`}
            onClick={() => setShowHint(false)}
          >
            <div className="hint-pill">
              <span className="hint-icon" aria-hidden="true">⟳</span>
              Hold &amp; drag to spin the gallery
            </div>
            <div className="hint-pill hint-pill--secondary">
              <span className="hint-icon" aria-hidden="true">↗</span>
              Tap the arrow to open details
            </div>
          </div>
        )}
      </div>

      {modalItem && (
        <ArtDetailModal
          item={modalItem}
          category={activeCategory}
          onClose={() => setModalItem(null)}
        />
      )}
    </div>
  );
}
